import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { reportLanguagePreference } from "@/utils/languagePref";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // 1. Saved preference always wins.
    const saved = localStorage.getItem("karine_language");
    if (saved === "en" || saved === "fr") return saved;

    // 2. First-visit only: auto-detect French speakers from the browser.
    //    Checks navigator.languages (most preferred first) then navigator.language.
    //    Matches any French variant: fr, fr-CA, fr-FR, fr-BE, fr-CH, etc.
    try {
      const candidates = [
        ...(Array.isArray(navigator.languages) ? navigator.languages : []),
        navigator.language,
      ].filter(Boolean);
      const prefersFrench = candidates.some((c) =>
        String(c).toLowerCase().startsWith("fr")
      );
      if (prefersFrench) return "fr";
    } catch (_) {
      // navigator may be unavailable in some SSR / sandbox contexts
    }
    return "en";
  });

  useEffect(() => {
    localStorage.setItem("karine_language", language);
    reportLanguagePreference("", language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const next = prev === "en" ? "fr" : "en";
      // Subtle confirmation so users know their choice is remembered next visit.
      toast.success(
        next === "fr"
          ? "Préférence enregistrée — affichage en français"
          : "Preference saved — switched to English",
        { duration: 2200 }
      );
      return next;
    });
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.en?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  en: {
    // Navigation
    "nav.promotions": "Promotions",
    "nav.services": "Services",
    "nav.about": "About",
    "nav.locations": "Locations",
    "nav.aftercare": "Prior & After Care",
    "nav.giftCards": "Gift Cards",
    "nav.finance": "Finance",
    "nav.chat": "Message Us",
    "nav.contact": "Contact",
    "nav.bookNow": "Book Now",
    "nav.search": "Search",
    "nav.precisionInjectionist": "Precision Injectionist",
    
    // Dropdown - Promotions
    "dropdown.currentPromotions": "Current Promotions",
    "dropdown.viewAllPromotions": "View All Promotions",
    "dropdown.saveOnCombo": "Save $99 on this popular combo",
    "dropdown.bestValueFiller": "Best value filler package",
    "dropdown.premiumFiller": "Premium filler package",
    "dropdown.ultimateSavings": "Ultimate savings package",
    
    // Dropdown - Services
    "dropdown.ourServices": "Our Services",
    "dropdown.viewAllServices": "View All Services",
    "dropdown.freeConsultations": "Free consultations available",
    "dropdown.neuromodulators": "Neuromodulators (Botox)",
    "dropdown.dermalFillers": "Dermal Fillers",
    "dropdown.sculptra": "Sculptra",
    "dropdown.microNeedling": "Medical Micro Needling",
    "dropdown.prpTherapy": "PRP Therapy",
    "dropdown.painManagement": "Pain Management",
    "dropdown.smoothWrinkles": "Smooth wrinkles & fine lines",
    "dropdown.restoreVolume": "Restore volume & contour",
    "dropdown.stimulateCollagen": "Stimulate natural collagen",
    "dropdown.skinRejuvenation": "Skin rejuvenation treatment",
    "dropdown.hairRestoration": "Hair restoration therapy",
    "dropdown.therapeuticRelief": "Therapeutic relief treatments",
    
    // Dropdown - About
    "dropdown.aboutKarine": "About Karine Lauzon RN",
    "dropdown.learnMore": "Learn More About Karine",
    "dropdown.registeredNurse": "Registered Nurse",
    "dropdown.rnDesc": "Licensed healthcare professional",
    "dropdown.aclsCertified": "ACLS Instructor",
    "dropdown.aclsDesc": "Teaches Advanced Cardiac Life Support",
    "dropdown.experience": "35+ Years Experience",
    "dropdown.experienceDesc": "Decades of clinical expertise",
    "dropdown.mercyAlumni": "Our Lady of Mercy Alumni",
    "dropdown.mercyDesc": "Nursing school graduate",
    
    // Dropdown - Locations
    "dropdown.ourLocations": "Our Locations",
    "dropdown.byAppointment": "By Appointment",
    "dropdown.bookAt": "Book at",
    
    // Dropdown - Aftercare
    "dropdown.aftercareGuides": "Aftercare Guides",
    "dropdown.viewFullGuide": "View Full Aftercare Guide",
    "dropdown.botoxAftercare": "Botox Aftercare",
    "dropdown.botoxDesc": "Post-treatment care for neuromodulators",
    "dropdown.fillerAftercare": "Filler Aftercare",
    "dropdown.fillerDesc": "Lip and facial filler care instructions",
    "dropdown.generalTips": "General Tips",
    "dropdown.generalDesc": "Universal post-treatment advice",
    
    // Dropdown - Gift Cards
    "dropdown.giftCertificates": "Gift Certificates",
    "dropdown.purchaseGiftCard": "Purchase Gift Card",
    "dropdown.giveGiftOfBeauty": "Give the gift of beauty",
    "dropdown.anyAmount": "Any Amount",
    "dropdown.chooseValue": "Choose your perfect gift value",
    "dropdown.validAllLocations": "Valid at All Locations",
    "dropdown.allThreeLocations": "Ottawa, Carleton Place & Kanata",
    "dropdown.noExpiration": "No Expiration",
    "dropdown.noRush": "Use anytime, no rush",
    "dropdown.emailDelivery": "Email Delivery",
    "dropdown.instantDigital": "Instant digital delivery available",
    "dropdown.learnMoreGiftCards": "Learn More About Gift Cards",
    
    // Dropdown - Finance
    "dropdown.flexiblePayment": "Flexible Payment Options",
    "dropdown.payIn4": "Pay in 4 Installments",
    "dropdown.shopNowPayLater": "Shop now, pay later in 4 interest-free payments",
    "dropdown.noExtraFees": "No extra fees when you pay on time",
    "dropdown.monthlyPayments": "Monthly Payment Plans",
    "dropdown.flexibleMonthly": "Flexible monthly payments via Medicard",
    "dropdown.quickApproval": "Quick online approval process",
    "dropdown.financeLarger": "Finance larger treatments over time",
    "dropdown.calculatePayments": "Calculate Your Payments",
    "dropdown.viewAllPaymentOptions": "View All Payment Options",
    
    // Dropdown - Chat
    "dropdown.getInTouch": "Get In Touch",
    "dropdown.goToContactForm": "Go to Contact Form",
    "dropdown.facebookMessenger": "Facebook Messenger",
    "dropdown.chatInstantly": "Chat with us instantly",
    "dropdown.callUs": "Call Us",
    "dropdown.textUs": "Text Us",
    "dropdown.textUsAt": "Text Us At",
    "dropdown.responseTime": "Response Time",
    "dropdown.usuallyWithin": "Usually within 1-2 hours",
    
    // Hero Section
    "hero.experience": "35+ Years Experience",
    "hero.precision": "Precision.",
    "hero.expertise": "Expertise.",
    "hero.naturalResults": "Natural Results.",
    "hero.welcome": "Welcome to Karine Lauzon RN Clinics — where advanced medical aesthetics meets compassionate care. Experience the artistry of injectable treatments delivered by a registered nurse with over three decades of clinical expertise.",
    "hero.viewServices": "View Services",
    "hero.bookNow": "Book Now",
    "hero.locations": "Locations",
    "hero.years": "Years",
    "hero.reviews": "5-Star Reviews",
    "hero.certified": "Certified",
    
    // Services Section
    "services.title": "OUR SERVICES",
    "services.heading": "Expert",
    "services.headingAccent": "Treatments",
    "services.description": "From cosmetic enhancements to therapeutic solutions, we offer a comprehensive range of injectable treatments tailored to your unique needs.",
    "services.expandAll": "Expand All",
    "services.collapseAll": "Collapse All",
    "services.add": "Add",
    "services.added": "Added",
    "services.servicesCount": "services",
    "services.pricingNote": "Prices may vary. Tax may apply where indicated. Book a",
    "services.freeConsultation": "free consultation",
    "services.toDiscuss": "to discuss your personalized treatment plan.",
    
    // About Section
    "about.title": "ABOUT KARINE",
    "about.heading": "Meet Your",
    "about.headingAccent": "Expert",
    "about.p1": "Karine Lauzon is a Registered Nurse with over 35 years of clinical experience, specializing in medical aesthetics and pain management. Her journey began at Carleton Place & District Hospital, where she dedicated 21 years to providing exceptional patient care.",
    "about.p2": "As an ACLS Certification Instructor — she teaches the Advanced Cardiac Life Support certification — Karine brings the highest standards of safety and expertise to every treatment. Her approach combines medical precision with an artistic eye, ensuring natural-looking results that enhance your unique beauty.",
    "about.p3": "Today, Karine operates three clinics across the Ottawa region, making premium injectable treatments accessible to more patients while maintaining the personalized care that defines her practice.",
    "about.yearsExp": "35+ Years",
    "about.yearsExpDesc": "Clinical nursing experience",
    "about.acls": "ACLS Certification Instructor",
    "about.aclsDesc": "Advanced Cardiovascular Life Support",
    "about.rn": "RN Licensed",
    "about.rnDesc": "Registered Nurse Professional",
    "about.rated": "5-Star Rated",
    "about.ratedDesc": "Over 100 patient reviews",
    "about.excellence": "of Excellence",
    
    // Locations Section
    "locations.title": "OUR LOCATIONS",
    "locations.heading": "Book",
    "locations.headingAccent": "Online",
    "locations.description": "Book your appointment with Karine Lauzon RN online by clicking the direct link to the location nearest you. You will see all available days and times as well as all treatments we provide with our pricing.",
    "locations.bookOnline": "Book Online",
    "locations.easyBooking": "Easy Online Booking",
    "locations.easyBookingDesc": "Select your preferred location, browse available treatments with pricing, and choose a time that works for you.",
    
    // Testimonials Section
    "testimonials.title": "TESTIMONIALS",
    "testimonials.heading": "What Our",
    "testimonials.headingAccent": "Clients Say",
    "testimonials.viewAll": "View All Reviews on Google",
    "testimonials.reviewsOn": "Reviews on Google",
    
    // Promotions Section - Summer Glow Event
    "promotions.springSpecial": "Summer Glow Event",
    "promotions.springRefresh": "Summer Glow",
    "promotions.refreshEvent": "Event",
    "promotions.freshStart": "Summer is the season of radiance — the perfect time to refresh your look and glow through every event on your calendar.",
    "promotions.springRefreshPackages": "Summer Botox Refresh",
    "promotions.packages": "Packages",
    "promotions.springFillerDeals": "Summer Filler",
    "promotions.deals": "Packages",
    "promotions.starter": "Starter",
    "promotions.miniRefresh": "Mini Refresh",
    "promotions.miniRefreshPlus": "Mini Refresh Plus",
    "promotions.mostPopular": "Most Popular",
    "promotions.totalFace": "The Total Face",
    "promotions.big3Areas": "The Big 3 Areas",
    "promotions.quickPickMeUps": "Quick Pick-Me-Ups",
    "promotions.completePackage": "Complete Package",
    "promotions.quickEasy": "Quick & Easy",
    "promotions.anyArea": "Any Area",
    "promotions.bestValue": "Best Value",
    "promotions.bundleDeal": "Bundle Deal",
    "promotions.bestPricing": "Best Pricing",
    "promotions.freeTox": "FREE 11's or Forehead",
    "promotions.bestPricingOption": "Best Pricing Option",
    "promotions.maySubstitute": "*May substitute 11's for Forehead",
    "promotions.lipFlipMay": "*Lip Flip may substitute Brow or Smile Lift",
    
    // Gift Certificate Section
    "gift.title": "GIFT CERTIFICATES",
    "gift.perfectGift": "The Perfect Gift",
    "gift.heading": "Gift",
    "gift.headingAccent": "Certificates",
    "gift.description": "Give the gift of beauty and confidence! Our gift cards can be used for any services at any of our 3 clinics. Perfect for birthdays, holidays, or just because.",
    "gift.purchase": "Purchase Gift Card",
    "gift.allLocations": "All 3 locations",
    "gift.anyAmount": "Any amount",
    "gift.anyService": "Any service",
    "gift.noExpiry": "No expiration",
    
    // Payment Section
    "payment.title": "PAYMENT OPTIONS",
    "payment.heading": "Flexible",
    "payment.headingAccent": "Financing",
    "payment.description": "We believe everyone deserves to feel confident. That's why we offer flexible payment options.",
    "payment.weAccept": "We Accept",
    
    // Aftercare Section
    "aftercare.title": "AFTERCARE",
    "aftercare.postTreatmentGuide": "Post-Treatment Guide",
    "aftercare.heading": "Aftercare",
    "aftercare.headingAccent": "Instructions",
    "aftercare.description": "Follow these guidelines for optimal results and a smooth recovery",
    "aftercare.neuromodulatorAftercare": "Neuromodulator Aftercare",
    "aftercare.botoxNuceiva": "Botox / Nuceiva / Dysport",
    "aftercare.fillerAftercare": "Dermal Filler Aftercare",
    "aftercare.lipsCheeJaw": "Lips / Cheeks / Jawline",
    "aftercare.first4Hours": "For the first 4 hours",
    "aftercare.first24Hours": "For the first 24 hours",
    "aftercare.first48Hours": "For the first 48 hours",
    "aftercare.stayUpright": "Stay upright (no lying down)",
    "aftercare.noRubbing": "Do not rub, press, or massage the treated areas",
    "aftercare.avoidHats": "Avoid hats, tight headbands, or anything pressing on the area",
    "aftercare.noStrenuous": "No strenuous exercise or heavy lifting",
    "aftercare.avoidAlcohol": "Avoid alcohol and blood thinners",
    "aftercare.noSaunas": "Avoid saunas, hot tubs, and excessive heat",
    "aftercare.sleepElevated": "Sleep with head elevated if possible",
    "aftercare.applyIce": "Apply ice gently if swelling occurs",
    "aftercare.avoidMakeup": "Avoid makeup on treated areas for 4 hours",
    "aftercare.stayHydrated": "Stay hydrated and avoid salty foods",
    "aftercare.normalActivities": "Normal activities can resume",
    "aftercare.fullResults": "Full results visible in 2 weeks for Botox, immediately for fillers",
    "aftercare.questionsContact": "Questions? Contact us anytime",
    "aftercare.important": "Important",
    "aftercare.contactIf": "Contact us immediately if you experience:",
    "aftercare.severeSwelling": "Severe swelling or bruising",
    "aftercare.unusualPain": "Unusual pain or discomfort",
    "aftercare.signsInfection": "Signs of infection",
    "aftercare.visionChanges": "Vision changes or severe headache",
    
    // Contact Section
    "contact.title": "GET IN TOUCH",
    "contact.heading": "Book Your",
    "contact.headingAccent": "Consultation",
    "contact.description": "Ready to start your journey? Fill out the form below and we'll get back to you to schedule your personalized consultation.",
    "contact.name": "Full Name",
    "contact.email": "Email Address",
    "contact.phone": "Phone Number",
    "contact.service": "Service of Interest",
    "contact.selectService": "Select a service",
    "contact.location": "Preferred Location",
    "contact.selectLocation": "Select a location",
    "contact.date": "Preferred Date (Optional)",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Tell us about your goals and any questions you have...",
    "contact.submit": "Submit Inquiry",
    "contact.sending": "Submitting...",
    "contact.success": "Message sent successfully!",
    "contact.error": "Failed to send message. Please try again.",
    
    // Footer
    "footer.rights": "All rights reserved.",
    "footer.tagline": "Precision. Expertise. Natural Results.",
    "footer.quickLinks": "Quick Links",
    "footer.services": "Services",
    "footer.contactInfo": "Contact",
    "footer.followUs": "Follow Us",
    
    // Booking Modal
    "booking.chooseLocation": "Choose a",
    "booking.locationAccent": "Location",
    "booking.selectClinic": "Select a clinic to view availability and book your appointment",
    "booking.bookHere": "Book Here",
    "booking.redirect": "You'll be redirected to our secure Square Appointments page to complete your booking.",
    
    // Search
    "search.placeholder": "Search services, locations, treatments...",
    "search.popular": "Popular Searches",
    "search.noResults": "No results found for",
    "search.trySearching": "Try searching for",
    
    // Misc
    "misc.note": "Note",
    "misc.learnMore": "Learn More",
    "misc.viewAll": "View All",
    "misc.close": "Close",
    "misc.pressEsc": "Press ESC to close",
    "misc.added": "Added",
    "misc.add": "Add",
    "misc.addToAppointment": "Add to Appointment",
    "misc.bookPromoNow": "Book Your Promotion Now",
    
    // TopBar
    "topbar.locations": "3 LOCATIONS:",
    "topbar.textUs": "Text Us:",
    
    // Footer Services
    "footer.botox": "Botox",
    "footer.dermalFillers": "Dermal Fillers",
    "footer.lipEnhancement": "Lip Enhancement",
    "footer.microNeedling": "Micro-Needling",
    "footer.prpTherapy": "PRP Therapy",
    "footer.painManagement": "Pain Management",
    "footer.weAccept": "We Accept",
    "footer.convenientPayment": "Convenient payment options for all treatments",
    
    // Contact Section
    "contact.contactInfo": "Contact Information",
    "contact.phone": "Phone",
    "contact.emailLabel": "Email",
    "contact.hours": "Hours",
    "contact.ourLocations": "Our Locations",
    "contact.toBookAppointment": "To book an appointment or see a list of services, click the location below:",
    "contact.bookOnline": "Book Online",
    "contact.downtown": "Downtown Carleton Place",
    "contact.sixthFloor": "Sixth Floor",
    "contact.mainFloor": "Main Floor",
    "contact.byAppointment": "By Appointment",
    "contact.thankYou": "Thank You!",
    "contact.submittedSuccess": "Your inquiry has been submitted successfully. We'll get back to you within 24-48 hours.",
    "contact.submitAnother": "Submit Another Inquiry",
    "contact.nameRequired": "Name is required",
    "contact.emailRequired": "Email is required",
    "contact.validEmail": "Please enter a valid email",
    "contact.phoneRequired": "Phone number is required",
    "contact.selectServiceError": "Please select a service",
    "contact.selectLocationError": "Please select a location",
    "contact.messageRequired": "Message is required",
    "contact.fillAllFields": "Please fill in all required fields",
    "contact.successToast": "Thank you! Your inquiry has been submitted. We'll contact you soon.",
    "contact.errorToast": "There was an error submitting your inquiry. Please try again.",
    
    // Aftercare Section
    "aftercare.forFirst4Hours": "For the first 4 hours",
    "aftercare.for24Hours": "For 24 hours",
    "aftercare.for48Hours": "For 48 hours",
    "aftercare.for2Weeks": "For 2 weeks",
    "aftercare.forFirst6Hours": "For the first 6 hours",
    "aftercare.whatsNormal": "What's normal",
    "aftercare.avoidFor2Weeks": "Avoid for 2 weeks",
    "aftercare.stayUprightNoLying": "Stay upright (no lying down)",
    "aftercare.doNotRub": "Do not rub, press, or massage the treated areas",
    "aftercare.avoidHatsTight": "Avoid hats, tight headbands, or anything pressing on the area",
    "aftercare.gentlyMove": "Gently move the treated muscles (frown, raise brows, squint) a few times",
    "aftercare.noExercise": "No exercise or heavy physical activity",
    "aftercare.noAlcohol": "No alcohol",
    "aftercare.noFacials": "No facials, massages, or skin treatments",
    "aftercare.avoidExcessiveHeat": "Avoid excessive heat (hot tubs, saunas, steam rooms, hot yoga)",
    "aftercare.avoidMakeupIfPossible": "Avoid makeup if possible for 4–6 hours",
    "aftercare.smallBumps": "Small bumps for 20–30 minutes",
    "aftercare.mildRedness": "Mild redness or pinpoint bruising",
    "aftercare.slightHeadache": "Slight headache or tight feeling",
    "aftercare.resultsBegin": "Results begin in 3–5 days, full effect at 10–14 days",
    "aftercare.facialTreatments": "Facial treatments",
    "aftercare.aggressiveSkinCare": "Aggressive skin care over injection points",
    "aftercare.lipCheekFacial": "Lip, Cheek & Facial Fillers",
    "aftercare.doNotTouch": "Do not touch, press, or massage the area unless instructed",
    "aftercare.avoidMakeupIfPossible2": "Avoid makeup if possible",
    "aftercare.applyColdCompress": "Apply cold compress gently if swollen (10 min on / 10 min off)",
    "aftercare.noExercise2": "No exercise",
    "aftercare.noAlcohol2": "No alcohol",
    "aftercare.avoidExcessiveHeat2": "Avoid excessive heat (sun, sauna, hot tubs)",
    "aftercare.sleepOnBack": "Sleep on your back if possible",
    "aftercare.avoidPressure": "Avoid pressure on treated areas",
    "aftercare.expectSwelling": "Expect swelling, firmness, mild asymmetry (normal)",
    "aftercare.bruisingMayAppear": "Bruising may appear — this is common",
    "aftercare.avoidDentalWork": "Avoid dental work",
    "aftercare.noFacialsLasers": "No facials, lasers, or skin treatments over the area",
    "aftercare.avoidAggressiveMassage": "Avoid aggressive massage",
    "aftercare.allowFillerSettle": "Allow filler to fully settle before judging results",
    "aftercare.swellingPeaks": "Swelling peaks at 24–48 hours",
    "aftercare.lipsMayFeelFirm": "Lips may feel firm for several days",
    "aftercare.minorAsymmetry": "Minor asymmetry during swelling phase",
    "aftercare.finalResult": "Final result at 10–14 days, not before",
    "aftercare.importantFollowUp": "Important: Follow-Up Appointments",
    "aftercare.followUpNote": "We have follow-up appointments between 14–21 days after initial treatments to allow for dosage adjustments. Any time after the 21 days, no possible adjustments can be done as you may develop Botox immunity and be unable to receive treatments for 1 year or more.",
    
    // Payment Section
    "payment.flexiblePayments": "FLEXIBLE PAYMENTS",
    "payment.paymentOptions": "Payment Options",
    "payment.webelieve": "We believe everyone deserves to look and feel their best. Choose the payment option that works best for you.",
    "payment.afterpayExamples": "Afterpay Payment Examples",
    "payment.treatmentTotal": "Treatment Total",
    "payment.fourPaymentsOf": "4 payments of",
    "payment.allMajorCards": "All Major Credit Cards Accepted",
    "payment.convenientPayment": "Convenient payment options for all treatments",
    "payment.afterpay": "Afterpay",
    "payment.fourInterestFree": "4 interest-free payments",
    "payment.medicard": "Medicard",
    "payment.quickEasyApproval": "Quick easy approval",
    "payment.allMajorCardsShort": "All Major Cards",
    "payment.visaMcAmexDebit": "Visa, MC, Amex, Debit",
    
    // Testimonials Section
    "testimonials.googleReviews": "Google",
    
    // Search Modal
    "search.trySearchingFor": "Try searching for \"Botox\", \"Lip\", \"Cheeks\", \"Filler\", or \"Ottawa\"",
  },
  
  fr: {
    // Navigation
    "nav.promotions": "Promotions",
    "nav.services": "Services",
    "nav.about": "À Propos",
    "nav.locations": "Emplacements",
    "nav.aftercare": "Soins avant & après",
    "nav.giftCards": "Cartes Cadeaux",
    "nav.finance": "Financement",
    "nav.chat": "Écrivez-Nous",
    "nav.contact": "Contact",
    "nav.bookNow": "Réserver",
    "nav.search": "Rechercher",
    "nav.precisionInjectionist": "Injectrice de Précision",
    
    // Dropdown - Promotions
    "dropdown.currentPromotions": "Promotions Actuelles",
    "dropdown.viewAllPromotions": "Voir Toutes les Promotions",
    "dropdown.saveOnCombo": "Économisez 99$ sur ce combo populaire",
    "dropdown.bestValueFiller": "Forfait filler meilleur rapport qualité-prix",
    "dropdown.premiumFiller": "Forfait filler premium",
    "dropdown.ultimateSavings": "Forfait économies ultimes",
    
    // Dropdown - Services
    "dropdown.ourServices": "Nos Services",
    "dropdown.viewAllServices": "Voir Tous les Services",
    "dropdown.freeConsultations": "Consultations gratuites disponibles",
    "dropdown.neuromodulators": "Neuromodulateurs (Botox)",
    "dropdown.dermalFillers": "Agents de Comblement",
    "dropdown.sculptra": "Sculptra",
    "dropdown.microNeedling": "Micro-Aiguilles Médicales",
    "dropdown.prpTherapy": "Thérapie PRP",
    "dropdown.painManagement": "Gestion de la Douleur",
    "dropdown.smoothWrinkles": "Lisser les rides et ridules",
    "dropdown.restoreVolume": "Restaurer le volume et les contours",
    "dropdown.stimulateCollagen": "Stimuler le collagène naturel",
    "dropdown.skinRejuvenation": "Traitement de rajeunissement cutané",
    "dropdown.hairRestoration": "Thérapie de restauration capillaire",
    "dropdown.therapeuticRelief": "Traitements de soulagement thérapeutique",
    
    // Dropdown - About
    "dropdown.aboutKarine": "À Propos de Karine Lauzon RN",
    "dropdown.learnMore": "En Savoir Plus sur Karine",
    "dropdown.registeredNurse": "Infirmière Autorisée",
    "dropdown.rnDesc": "Professionnelle de santé licenciée",
    "dropdown.aclsCertified": "Instructrice ACLS",
    "dropdown.aclsDesc": "Formée en support vital avancé",
    "dropdown.experience": "35+ Ans d'Expérience",
    "dropdown.experienceDesc": "Décennies d'expertise clinique",
    "dropdown.mercyAlumni": "Ancienne de Our Lady of Mercy",
    "dropdown.mercyDesc": "Diplômée en soins infirmiers",
    
    // Dropdown - Locations
    "dropdown.ourLocations": "Nos Emplacements",
    "dropdown.byAppointment": "Sur Rendez-vous",
    "dropdown.bookAt": "Réserver à",
    
    // Dropdown - Aftercare
    "dropdown.aftercareGuides": "Guides de Soins Post-Traitement",
    "dropdown.viewFullGuide": "Voir le Guide Complet",
    "dropdown.botoxAftercare": "Soins Post-Botox",
    "dropdown.botoxDesc": "Soins post-traitement pour neuromodulateurs",
    "dropdown.fillerAftercare": "Soins Post-Filler",
    "dropdown.fillerDesc": "Instructions pour les agents de comblement",
    "dropdown.generalTips": "Conseils Généraux",
    "dropdown.generalDesc": "Conseils universels post-traitement",
    
    // Dropdown - Gift Cards
    "dropdown.giftCertificates": "Cartes Cadeaux",
    "dropdown.purchaseGiftCard": "Acheter une Carte Cadeau",
    "dropdown.giveGiftOfBeauty": "Offrez le cadeau de la beauté",
    "dropdown.anyAmount": "Tout Montant",
    "dropdown.chooseValue": "Choisissez la valeur parfaite",
    "dropdown.validAllLocations": "Valide Partout",
    "dropdown.allThreeLocations": "Ottawa, Carleton Place & Kanata",
    "dropdown.noExpiration": "Sans Expiration",
    "dropdown.noRush": "Utilisez quand vous voulez",
    "dropdown.emailDelivery": "Livraison par Courriel",
    "dropdown.instantDigital": "Livraison numérique instantanée",
    "dropdown.learnMoreGiftCards": "En Savoir Plus sur les Cartes Cadeaux",
    
    // Dropdown - Finance
    "dropdown.flexiblePayment": "Options de Paiement Flexibles",
    "dropdown.payIn4": "Payez en 4 Versements",
    "dropdown.shopNowPayLater": "Achetez maintenant, payez plus tard en 4 versements sans intérêt",
    "dropdown.noExtraFees": "Aucuns frais supplémentaires si payé à temps",
    "dropdown.monthlyPayments": "Plans de Paiement Mensuels",
    "dropdown.flexibleMonthly": "Paiements mensuels flexibles via Medicard",
    "dropdown.quickApproval": "Processus d'approbation rapide en ligne",
    "dropdown.financeLarger": "Financez les traitements plus importants",
    "dropdown.calculatePayments": "Calculez Vos Paiements",
    "dropdown.viewAllPaymentOptions": "Voir Toutes les Options de Paiement",
    
    // Dropdown - Chat
    "dropdown.getInTouch": "Contactez-Nous",
    "dropdown.goToContactForm": "Aller au Formulaire de Contact",
    "dropdown.facebookMessenger": "Facebook Messenger",
    "dropdown.chatInstantly": "Discutez avec nous instantanément",
    "dropdown.callUs": "Appelez-Nous",
    "dropdown.textUs": "Textez-Nous",
    "dropdown.textUsAt": "Textez-Nous Au",
    "dropdown.responseTime": "Temps de Réponse",
    "dropdown.usuallyWithin": "Habituellement sous 1-2 heures",
    
    // Hero Section
    "hero.experience": "35+ Ans d'Expérience",
    "hero.precision": "Précision.",
    "hero.expertise": "Expertise.",
    "hero.naturalResults": "Résultats Naturels.",
    "hero.welcome": "Bienvenue aux Cliniques Karine Lauzon RN — où l'esthétique médicale avancée rencontre des soins attentionnés. Découvrez l'art des traitements injectables prodigués par une infirmière autorisée avec plus de trois décennies d'expertise clinique.",
    "hero.viewServices": "Voir les Services",
    "hero.bookNow": "Réserver",
    "hero.locations": "Emplacements",
    "hero.years": "Ans",
    "hero.reviews": "Avis 5 Étoiles",
    "hero.certified": "Certifiée",
    
    // Services Section
    "services.title": "NOS SERVICES",
    "services.heading": "Traitements",
    "services.headingAccent": "Experts",
    "services.description": "Des améliorations cosmétiques aux solutions thérapeutiques, nous offrons une gamme complète de traitements injectables adaptés à vos besoins uniques.",
    "services.expandAll": "Tout Développer",
    "services.collapseAll": "Tout Réduire",
    "services.add": "Ajouter",
    "services.added": "Ajouté",
    "services.servicesCount": "services",
    "services.pricingNote": "Les prix peuvent varier. Les taxes peuvent s'appliquer. Réservez une",
    "services.freeConsultation": "consultation gratuite",
    "services.toDiscuss": "pour discuter de votre plan de traitement personnalisé.",
    
    // About Section
    "about.title": "À PROPOS DE KARINE",
    "about.heading": "Rencontrez Votre",
    "about.headingAccent": "Experte",
    "about.p1": "Karine Lauzon est une infirmière autorisée avec plus de 35 ans d'expérience clinique, spécialisée en esthétique médicale et gestion de la douleur. Son parcours a commencé à l'Hôpital de Carleton Place et District, où elle a consacré 21 ans à fournir des soins exceptionnels aux patients.",
    "about.p2": "En tant qu'instructrice certifiée ACLS, Karine apporte les plus hauts standards de sécurité et d'expertise à chaque traitement. Son approche combine précision médicale et œil artistique, garantissant des résultats naturels qui rehaussent votre beauté unique.",
    "about.p3": "Aujourd'hui, Karine opère trois cliniques dans la région d'Ottawa, rendant les traitements injectables premium accessibles à plus de patients tout en maintenant les soins personnalisés qui définissent sa pratique.",
    "about.yearsExp": "35+ Ans",
    "about.yearsExpDesc": "d'expérience clinique en soins infirmiers",
    "about.acls": "Instructrice de certification ACLS",
    "about.aclsDesc": "Support Cardiovasculaire Avancé",
    "about.rn": "Infirmière Autorisée",
    "about.rnDesc": "Professionnelle RN Licenciée",
    "about.rated": "5 Étoiles",
    "about.ratedDesc": "Plus de 100 avis patients",
    "about.excellence": "d'Excellence",
    
    // Locations Section
    "locations.title": "NOS EMPLACEMENTS",
    "locations.heading": "Réservez",
    "locations.headingAccent": "en Ligne",
    "locations.description": "Réservez votre rendez-vous avec Karine Lauzon RN en ligne en cliquant sur le lien direct vers l'emplacement le plus près de chez vous. Vous verrez tous les jours et heures disponibles ainsi que tous les traitements avec nos prix.",
    "locations.bookOnline": "Réserver en Ligne",
    "locations.easyBooking": "Réservation en Ligne Facile",
    "locations.easyBookingDesc": "Sélectionnez votre emplacement préféré, parcourez les traitements disponibles avec les prix, et choisissez un moment qui vous convient.",
    
    // Testimonials Section
    "testimonials.title": "TÉMOIGNAGES",
    "testimonials.heading": "Ce que Nos",
    "testimonials.headingAccent": "Clients Disent",
    "testimonials.viewAll": "Voir Tous les Avis sur Google",
    "testimonials.reviewsOn": "Avis sur Google",
    
    // Promotions Section - Summer Glow Event
    "promotions.springSpecial": "Événement Éclat d'Été",
    "promotions.springRefresh": "Éclat d'",
    "promotions.refreshEvent": "Été",
    "promotions.freshStart": "L'été est la saison du rayonnement — le moment idéal pour rafraîchir votre look avant chaque événement de votre calendrier.",
    "promotions.springRefreshPackages": "Forfaits Botox Été",
    "promotions.packages": "",
    "promotions.springFillerDeals": "Forfaits Filler",
    "promotions.deals": "Été",
    "promotions.starter": "Débutant",
    "promotions.miniRefresh": "Mini Rafraîchissement",
    "promotions.miniRefreshPlus": "Mini Rafraîchissement Plus",
    "promotions.mostPopular": "Plus Populaire",
    "promotions.totalFace": "Le Visage Complet",
    "promotions.big3Areas": "Les 3 Grandes Zones",
    "promotions.quickPickMeUps": "Retouches Rapides",
    "promotions.completePackage": "Forfait Complet",
    "promotions.quickEasy": "Rapide & Facile",
    "promotions.anyArea": "Toute Zone",
    "promotions.bestValue": "Meilleur Rapport Qualité-Prix",
    "promotions.bundleDeal": "Offre Groupée",
    "promotions.bestPricing": "Meilleur Prix",
    "promotions.freeTox": "11's ou Front GRATUIT",
    "promotions.bestPricingOption": "Meilleure Option de Prix",
    "promotions.maySubstitute": "*Peut remplacer 11's par le Front",
    "promotions.lipFlipMay": "*Le Lip Flip peut remplacer Brow ou Smile Lift",
    
    // Gift Certificate Section
    "gift.title": "CARTES CADEAUX",
    "gift.perfectGift": "Le Cadeau Parfait",
    "gift.heading": "Cartes",
    "gift.headingAccent": "Cadeaux",
    "gift.description": "Offrez le cadeau de la beauté et de la confiance! Nos cartes-cadeaux peuvent être utilisées pour tous les services dans nos 3 cliniques. Parfait pour les anniversaires, les fêtes, ou simplement pour faire plaisir.",
    "gift.purchase": "Acheter une Carte Cadeau",
    "gift.allLocations": "Les 3 emplacements",
    "gift.anyAmount": "Tout montant",
    "gift.anyService": "Tout service",
    "gift.noExpiry": "Sans expiration",
    
    // Payment Section
    "payment.title": "OPTIONS DE PAIEMENT",
    "payment.heading": "Financement",
    "payment.headingAccent": "Flexible",
    "payment.description": "Nous croyons que tout le monde mérite de se sentir confiant. C'est pourquoi nous offrons des options de paiement flexibles.",
    "payment.weAccept": "Nous Acceptons",
    
    // Aftercare Section
    "aftercare.title": "SOINS POST-TRAITEMENT",
    "aftercare.postTreatmentGuide": "Guide Post-Traitement",
    "aftercare.heading": "Instructions",
    "aftercare.headingAccent": "de Soins",
    "aftercare.description": "Suivez ces directives pour des résultats optimaux et une récupération en douceur",
    "aftercare.neuromodulatorAftercare": "Soins Post-Neuromodulateur",
    "aftercare.botoxNuceiva": "Botox / Nuceiva / Dysport",
    "aftercare.fillerAftercare": "Soins Post-Agent de Comblement",
    "aftercare.lipsCheeJaw": "Lèvres / Joues / Mâchoire",
    "aftercare.first4Hours": "Pour les 4 premières heures",
    "aftercare.first24Hours": "Pour les 24 premières heures",
    "aftercare.first48Hours": "Pour les 48 premières heures",
    "aftercare.stayUpright": "Restez debout (ne vous allongez pas)",
    "aftercare.noRubbing": "Ne pas frotter, presser ou masser les zones traitées",
    "aftercare.avoidHats": "Évitez les chapeaux, bandeaux serrés ou tout ce qui presse sur la zone",
    "aftercare.noStrenuous": "Pas d'exercice intense ou de levage lourd",
    "aftercare.avoidAlcohol": "Évitez l'alcool et les anticoagulants",
    "aftercare.noSaunas": "Évitez les saunas, bains chauds et chaleur excessive",
    "aftercare.sleepElevated": "Dormez avec la tête surélevée si possible",
    "aftercare.applyIce": "Appliquez doucement de la glace en cas de gonflement",
    "aftercare.avoidMakeup": "Évitez le maquillage sur les zones traitées pendant 4 heures",
    "aftercare.stayHydrated": "Restez hydraté et évitez les aliments salés",
    "aftercare.normalActivities": "Les activités normales peuvent reprendre",
    "aftercare.fullResults": "Résultats complets visibles en 2 semaines pour le Botox, immédiatement pour les fillers",
    "aftercare.questionsContact": "Des questions? Contactez-nous à tout moment",
    "aftercare.important": "Important",
    "aftercare.contactIf": "Contactez-nous immédiatement si vous ressentez:",
    "aftercare.severeSwelling": "Gonflement ou ecchymoses sévères",
    "aftercare.unusualPain": "Douleur ou inconfort inhabituel",
    "aftercare.signsInfection": "Signes d'infection",
    "aftercare.visionChanges": "Changements de vision ou maux de tête sévères",
    
    // Contact Section
    "contact.title": "CONTACTEZ-NOUS",
    "contact.heading": "Réservez Votre",
    "contact.headingAccent": "Consultation",
    "contact.description": "Prêt à commencer votre parcours? Remplissez le formulaire ci-dessous et nous vous recontacterons pour planifier votre consultation personnalisée.",
    "contact.name": "Nom Complet",
    "contact.email": "Adresse Courriel",
    "contact.phone": "Numéro de Téléphone",
    "contact.service": "Service d'Intérêt",
    "contact.selectService": "Sélectionnez un service",
    "contact.location": "Emplacement Préféré",
    "contact.selectLocation": "Sélectionnez un emplacement",
    "contact.date": "Date Préférée (Optionnel)",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Parlez-nous de vos objectifs et de vos questions...",
    "contact.submit": "Soumettre la Demande",
    "contact.sending": "Envoi en cours...",
    "contact.success": "Message envoyé avec succès!",
    "contact.error": "Échec de l'envoi. Veuillez réessayer.",
    
    // Footer
    "footer.rights": "Tous droits réservés.",
    "footer.tagline": "Précision. Expertise. Résultats Naturels.",
    "footer.quickLinks": "Liens Rapides",
    "footer.services": "Services",
    "footer.contactInfo": "Contact",
    "footer.followUs": "Suivez-Nous",
    
    // Booking Modal
    "booking.chooseLocation": "Choisissez un",
    "booking.locationAccent": "Emplacement",
    "booking.selectClinic": "Sélectionnez une clinique pour voir les disponibilités et réserver",
    "booking.bookHere": "Réserver Ici",
    "booking.redirect": "Vous serez redirigé vers notre page sécurisée Square Appointments.",
    
    // Search
    "search.placeholder": "Rechercher services, emplacements, traitements...",
    "search.popular": "Recherches Populaires",
    "search.noResults": "Aucun résultat trouvé pour",
    "search.trySearching": "Essayez de rechercher",
    
    // Misc
    "misc.note": "Note",
    "misc.learnMore": "En Savoir Plus",
    "misc.viewAll": "Voir Tout",
    "misc.close": "Fermer",
    "misc.pressEsc": "Appuyez sur ESC pour fermer",
    "misc.added": "Ajouté",
    "misc.add": "Ajouter",
    "misc.addToAppointment": "Ajouter au Rendez-vous",
    "misc.bookPromoNow": "Réservez Votre Promotion",
    
    // TopBar
    "topbar.locations": "3 EMPLACEMENTS:",
    "topbar.textUs": "Textez-nous:",
    
    // Footer Services
    "footer.botox": "Botox",
    "footer.dermalFillers": "Agents de Comblement",
    "footer.lipEnhancement": "Augmentation des Lèvres",
    "footer.microNeedling": "Micro-Aiguilles",
    "footer.prpTherapy": "Thérapie PRP",
    "footer.painManagement": "Gestion de la Douleur",
    "footer.weAccept": "Nous Acceptons",
    "footer.convenientPayment": "Options de paiement pratiques pour tous les traitements",
    
    // Contact Section
    "contact.contactInfo": "Coordonnées",
    "contact.phone": "Téléphone",
    "contact.emailLabel": "Courriel",
    "contact.hours": "Heures",
    "contact.ourLocations": "Nos Emplacements",
    "contact.toBookAppointment": "Pour réserver un rendez-vous ou voir la liste des services, cliquez sur l'emplacement ci-dessous:",
    "contact.bookOnline": "Réserver en Ligne",
    "contact.downtown": "Centre-ville de Carleton Place",
    "contact.sixthFloor": "Sixième étage, Clinique 603",
    "contact.mainFloor": "Rez-de-chaussée, Clinique G2",
    "contact.byAppointment": "Sur Rendez-vous",
    "contact.thankYou": "Merci!",
    "contact.submittedSuccess": "Votre demande a été soumise avec succès. Nous vous répondrons dans les 24-48 heures.",
    "contact.submitAnother": "Soumettre une Autre Demande",
    "contact.nameRequired": "Le nom est requis",
    "contact.emailRequired": "Le courriel est requis",
    "contact.validEmail": "Veuillez entrer un courriel valide",
    "contact.phoneRequired": "Le numéro de téléphone est requis",
    "contact.selectServiceError": "Veuillez sélectionner un service",
    "contact.selectLocationError": "Veuillez sélectionner un emplacement",
    "contact.messageRequired": "Le message est requis",
    "contact.fillAllFields": "Veuillez remplir tous les champs requis",
    "contact.successToast": "Merci! Votre demande a été soumise. Nous vous contacterons bientôt.",
    "contact.errorToast": "Une erreur s'est produite. Veuillez réessayer.",
    
    // Aftercare Section
    "aftercare.forFirst4Hours": "Pour les 4 premières heures",
    "aftercare.for24Hours": "Pour 24 heures",
    "aftercare.for48Hours": "Pour 48 heures",
    "aftercare.for2Weeks": "Pour 2 semaines",
    "aftercare.forFirst6Hours": "Pour les 6 premières heures",
    "aftercare.whatsNormal": "Ce qui est normal",
    "aftercare.avoidFor2Weeks": "Évitez pendant 2 semaines",
    "aftercare.stayUprightNoLying": "Restez debout (ne vous allongez pas)",
    "aftercare.doNotRub": "Ne pas frotter, presser ou masser les zones traitées",
    "aftercare.avoidHatsTight": "Évitez les chapeaux, bandeaux serrés ou tout ce qui presse sur la zone",
    "aftercare.gentlyMove": "Bougez doucement les muscles traités (froncez, levez les sourcils, plissez) quelques fois",
    "aftercare.noExercise": "Pas d'exercice ou d'activité physique intense",
    "aftercare.noAlcohol": "Pas d'alcool",
    "aftercare.noFacials": "Pas de soins du visage, massages ou traitements de la peau",
    "aftercare.avoidExcessiveHeat": "Évitez la chaleur excessive (bains chauds, saunas, hammams, yoga chaud)",
    "aftercare.avoidMakeupIfPossible": "Évitez le maquillage si possible pendant 4–6 heures",
    "aftercare.smallBumps": "Petites bosses pendant 20–30 minutes",
    "aftercare.mildRedness": "Légère rougeur ou ecchymoses ponctuelles",
    "aftercare.slightHeadache": "Léger mal de tête ou sensation de tension",
    "aftercare.resultsBegin": "Les résultats commencent en 3–5 jours, effet complet à 10–14 jours",
    "aftercare.facialTreatments": "Soins du visage",
    "aftercare.aggressiveSkinCare": "Soins agressifs sur les points d'injection",
    "aftercare.lipCheekFacial": "Fillers pour lèvres, joues et visage",
    "aftercare.doNotTouch": "Ne pas toucher, presser ou masser la zone sauf instruction contraire",
    "aftercare.avoidMakeupIfPossible2": "Évitez le maquillage si possible",
    "aftercare.applyColdCompress": "Appliquez une compresse froide doucement si gonflement (10 min on / 10 min off)",
    "aftercare.noExercise2": "Pas d'exercice",
    "aftercare.noAlcohol2": "Pas d'alcool",
    "aftercare.avoidExcessiveHeat2": "Évitez la chaleur excessive (soleil, sauna, bains chauds)",
    "aftercare.sleepOnBack": "Dormez sur le dos si possible",
    "aftercare.avoidPressure": "Évitez la pression sur les zones traitées",
    "aftercare.expectSwelling": "Attendez-vous à un gonflement, fermeté, légère asymétrie (normal)",
    "aftercare.bruisingMayAppear": "Des ecchymoses peuvent apparaître — c'est courant",
    "aftercare.avoidDentalWork": "Évitez les soins dentaires",
    "aftercare.noFacialsLasers": "Pas de soins du visage, lasers ou traitements cutanés sur la zone",
    "aftercare.avoidAggressiveMassage": "Évitez les massages agressifs",
    "aftercare.allowFillerSettle": "Laissez le filler se stabiliser avant de juger les résultats",
    "aftercare.swellingPeaks": "Le gonflement atteint son pic à 24–48 heures",
    "aftercare.lipsMayFeelFirm": "Les lèvres peuvent sembler fermes pendant plusieurs jours",
    "aftercare.minorAsymmetry": "Légère asymétrie pendant la phase de gonflement",
    "aftercare.finalResult": "Résultat final à 10–14 jours, pas avant",
    "aftercare.importantFollowUp": "Important: Rendez-vous de Suivi",
    "aftercare.followUpNote": "Nous avons des rendez-vous de suivi entre 14–21 jours après les traitements initiaux pour permettre les ajustements de dosage. Après 21 jours, aucun ajustement n'est possible car vous pourriez développer une immunité au Botox et être incapable de recevoir des traitements pendant 1 an ou plus.",
    
    // Payment Section
    "payment.flexiblePayments": "PAIEMENTS FLEXIBLES",
    "payment.paymentOptions": "Options de Paiement",
    "payment.webelieve": "Nous croyons que tout le monde mérite d'être et de se sentir au mieux. Choisissez l'option de paiement qui vous convient.",
    "payment.afterpayExamples": "Exemples de Paiement Afterpay",
    "payment.treatmentTotal": "Total du Traitement",
    "payment.fourPaymentsOf": "4 paiements de",
    "payment.allMajorCards": "Toutes les Cartes de Crédit Majeures Acceptées",
    "payment.convenientPayment": "Options de paiement pratiques pour tous les traitements",
    "payment.afterpay": "Afterpay",
    "payment.fourInterestFree": "4 paiements sans intérêt",
    "payment.medicard": "Medicard",
    "payment.quickEasyApproval": "Approbation rapide et facile",
    "payment.allMajorCardsShort": "Toutes les Cartes Majeures",
    "payment.visaMcAmexDebit": "Visa, MC, Amex, Débit",
    
    // Testimonials Section
    "testimonials.googleReviews": "Google",
    
    // Search Modal
    "search.trySearchingFor": "Essayez de rechercher \"Botox\", \"Lèvres\", \"Joues\", \"Filler\", ou \"Ottawa\"",
  },
};

export default LanguageContext;
